import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Activity,
  ArrowLeft,
  Sparkles,
  CalendarRange,
  Link2,
  Target,
} from "lucide-react";
import { FinanceKpiGrid } from "@/components/finance/FinanceKpiGrid";
import { CfoInsights } from "@/components/finance/CfoInsights";
import { RepassesTable } from "@/components/finance/RepassesTable";
import { RevenueChart } from "@/components/finance/RevenueChart";
import { ConvenioComparison } from "@/components/finance/ConvenioComparison";
import { useClinic } from "@/store/ClinicContext";
import { usePlan } from "@/store/PlanContext";
import { PlanPaywall } from "@/components/plan/PlanGating";
import { generateRepasseReceipt, generateExtratoConsolidado } from "@/utils/receipt";
import {
  FINANCE_PERIODS,
  PERIOD_MULTIPLIER,
  FINANCE_BASE,
  MONTHLY_GOAL,
  INITIAL_REPASSES,
  formatBRL,
} from "@/data/financeData";

export default function Financeiro() {
  const [period, setPeriod] = useState("mes");
  const [repasses, setRepasses] = useState(INITIAL_REPASSES);
  const { todayCash } = useClinic();
  const { isMax } = usePlan();

  const activePeriod = FINANCE_PERIODS.find((p) => p.id === period);

  const financeData = useMemo(() => {
    const m = PERIOD_MULTIPLIER[period];
    // Faturamento do dia (agenda) alimenta o DRE apenas na visão "Este Mês"
    const extraGross = period === "mes" ? todayCash.gross : 0;
    const extraRepasse = period === "mes" ? todayCash.repasseMedico : 0;
    const extraNet = period === "mes" ? todayCash.liquidoClinica : 0;
    return {
      faturamentoBruto: FINANCE_BASE.faturamentoBruto * m + extraGross,
      repasses: FINANCE_BASE.repasses * m + extraRepasse,
      custos: FINANCE_BASE.custos * m,
      lucroLiquido: FINANCE_BASE.lucroLiquido * m + extraNet,
      previsaoCaixa: FINANCE_BASE.previsaoCaixa * (period === "mes" ? 1 : 1.1) + extraNet,
      margem: FINANCE_BASE.margem,
      faturamentoVariacao: FINANCE_BASE.faturamentoVariacao,
    };
  }, [period, todayCash]);

  const totalLiberado = useMemo(
    () =>
      repasses
        .filter((r) => r.status === "pago")
        .reduce((s, r) => s + r.valorLiquido, 0),
    [repasses]
  );

  const releaseRepasse = (id) => {
    const target = repasses.find((r) => r.id === id);
    if (!target) return;
    setRepasses((prev) =>
      prev.map((r) => (r.id === id ? { ...r, status: "pago" } : r))
    );
    generateRepasseReceipt(target);
    toast.success("Repasse liberado via Pix!", {
      description: `${formatBRL(target.valorLiquido)} enviados para ${target.doctor}. Recibo PDF gerado.`,
    });
  };

  const exportExtrato = () => {
    generateExtratoConsolidado(repasses, `${activePeriod.label} · ${activePeriod.sub}`);
    toast.success("Extrato consolidado exportado!", {
      description: `PDF com ${repasses.length} repasses do período gerado.`,
    });
  };

  const goal = MONTHLY_GOAL * PERIOD_MULTIPLIER[period];
  const goalProgress = Math.min(100, Math.round((financeData.faturamentoBruto / goal) * 100));

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      {/* Topbar de governança */}
      <header className="sticky top-0 z-30 border-b border-slate-200/70 bg-white/80 backdrop-blur-md">
        <div className="mx-auto flex max-w-[1400px] flex-col gap-4 px-5 py-3.5 lg:flex-row lg:items-center lg:justify-between md:px-8">
          {/* Left */}
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-blue-600 shadow-[0_4px_12px_rgba(37,99,235,0.35)]">
              <Activity className="h-5 w-5 text-white" strokeWidth={2.5} />
            </div>
            <div className="flex items-center gap-2">
              <span className="font-heading text-xl font-extrabold tracking-tight text-slate-900">
                VeloClin
              </span>
              <span className="rounded-md border border-slate-200 bg-slate-50 px-1.5 py-0.5 text-[10px] font-semibold text-slate-500">
                v1.0 MVP
              </span>
            </div>
          </div>

          {/* Center: period filter */}
          <div className="flex items-center gap-1 rounded-xl border border-slate-200/80 bg-white p-1 shadow-sm">
            {FINANCE_PERIODS.map((p) => (
              <button
                key={p.id}
                data-testid={`period-${p.id}`}
                onClick={() => setPeriod(p.id)}
                className={`rounded-lg px-3 py-1.5 text-sm font-semibold transition-colors duration-200 ${
                  period === p.id
                    ? "bg-blue-600 text-white shadow-sm"
                    : "text-slate-500 hover:bg-slate-100 hover:text-slate-900"
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>

          {/* Right */}
          <div className="flex items-center gap-3">
            <div
              data-testid="cfo-status"
              className="hidden items-center gap-2 rounded-full border border-emerald-200/70 bg-emerald-50 px-3 py-1.5 sm:flex"
            >
              <span className="relative flex h-2 w-2">
                <span className="pulse-ring absolute inline-flex h-full w-full rounded-full bg-emerald-500 opacity-75" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" />
              </span>
              <Sparkles className="h-3.5 w-3.5 text-emerald-600" />
              <span className="text-xs font-semibold text-emerald-700">
                CFO IA Ativo · tempo real
              </span>
            </div>
            <Link
              to="/agenda"
              data-testid="back-to-agenda-fin"
              className="flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3 py-1.5 text-sm font-semibold text-slate-600 transition-colors duration-200 hover:bg-slate-50 hover:text-slate-900"
            >
              <ArrowLeft className="h-4 w-4" />
              Agenda
            </Link>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-[1400px] space-y-6 px-5 py-6 md:px-8 md:py-8">
        {/* Title */}
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-2">
            <h1 className="font-heading text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
              CFO de Bolso
            </h1>
            <span className="flex items-center gap-1 rounded-full bg-blue-50 px-2.5 py-1 text-xs font-semibold text-blue-700">
              <CalendarRange className="h-3 w-3" />
              {activePeriod.label} · {activePeriod.sub}
            </span>
          </div>
          <p className="text-sm text-slate-500">
            Visão de águia sobre a saúde financeira da clínica.
          </p>
        </div>

        {!isMax && (
          <>
            <div data-testid="basic-finance" className="rounded-2xl border border-slate-200/70 bg-white p-6 shadow-[0_2px_8px_rgba(0,0,0,0.04)]">
              <h2 className="font-heading text-base font-bold text-slate-900">Saúde Financeira (Basic)</h2>
              <p className="text-xs text-slate-500">Resumo simples do caixa do dia — incluído no Plano Basic.</p>
              <div className="mt-4 grid grid-cols-2 gap-3">
                <div className="rounded-xl border border-slate-100 bg-slate-50/60 p-4">
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">Faturamento de hoje</p>
                  <p className="font-mono-num mt-1 text-xl font-bold text-slate-900">{formatBRL(todayCash.gross)}</p>
                </div>
                <div className="rounded-xl border border-slate-100 bg-slate-50/60 p-4">
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">Pagamentos no caixa</p>
                  <p className="font-mono-num mt-1 text-xl font-bold text-emerald-600">{todayCash.count}</p>
                </div>
              </div>
            </div>
            <PlanPaywall
              testId="paywall-cfo"
              title="CFO de Bolso é exclusivo do Plano Max"
              desc="Tenha DRE preditivo, gráficos de rentabilidade, gestão avançada de repasses de sócios e auditoria de convênios (antiglosas) — tudo no Plano Max."
            />
          </>
        )}

        {isMax && (
          <>
        {/* Meta de Faturamento */}
        <div
          data-testid="meta-progress"
          className="rounded-2xl border border-slate-200/70 bg-white p-5 shadow-[0_2px_8px_rgba(0,0,0,0.04)] md:p-6"
        >
          <div className="mb-3 flex flex-wrap items-end justify-between gap-2">
            <div className="flex items-center gap-2">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-50">
                <Target className="h-4 w-4 text-blue-600" />
              </div>
              <div>
                <p className="font-heading text-base font-bold text-slate-900">
                  Meta de Faturamento
                </p>
                <p className="text-xs text-slate-500">
                  {activePeriod.label} · meta de{" "}
                  <span className="font-mono-num font-semibold text-slate-600">
                    {formatBRL(goal)}
                  </span>
                </p>
              </div>
            </div>
            <div className="text-right">
              <p className="font-mono-num text-xl font-bold text-slate-900" data-testid="meta-value">
                {formatBRL(financeData.faturamentoBruto)}
              </p>
              <p className="text-xs font-semibold text-emerald-600">{goalProgress}% da meta</p>
            </div>
          </div>
          <div className="h-3 w-full overflow-hidden rounded-full bg-slate-100">
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${goalProgress}%` }}
              transition={{ duration: 0.7, ease: "easeOut" }}
              className={`h-full rounded-full ${
                goalProgress >= 100 ? "bg-emerald-500" : "bg-blue-600"
              }`}
            />
          </div>
          <p className="mt-2 text-xs text-slate-400">
            {goalProgress >= 100
              ? "Meta batida! Excelente performance da clínica. 🎉"
              : `Faltam ${formatBRL(Math.max(0, goal - financeData.faturamentoBruto))} para atingir a meta.`}
          </p>
        </div>

        {/* KPIs */}
        <FinanceKpiGrid data={financeData} />

        {/* Conexão com a agenda */}
        {period === "mes" && todayCash.gross > 0 && (
          <div
            data-testid="connected-banner"
            className="flex items-center gap-2.5 rounded-xl border border-blue-200/60 bg-blue-50/60 px-4 py-3"
          >
            <Link2 className="h-4 w-4 shrink-0 text-blue-600" />
            <p className="text-sm text-blue-800">
              Conectado à Agenda · Faturamento de hoje de{" "}
              <span className="font-mono-num font-bold">{formatBRL(todayCash.gross)}</span> (
              {todayCash.count} pagamento{todayCash.count > 1 ? "s" : ""}) já incluído no DRE
              automaticamente.
            </p>
          </div>
        )}

        {/* Gráficos */}
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-5">
          <div className="lg:col-span-3">
            <RevenueChart />
          </div>
          <div className="lg:col-span-2">
            <ConvenioComparison />
          </div>
        </div>

        {/* Insights */}
        <CfoInsights />

        {/* Repasses */}
        <RepassesTable
          repasses={repasses}
          onRelease={releaseRepasse}
          onReceipt={generateRepasseReceipt}
          onExport={exportExtrato}
        />

        {totalLiberado > 0 && (
          <p className="text-right text-sm text-slate-500" data-testid="total-liberado">
            Total liberado nesta sessão:{" "}
            <span className="font-mono-num font-bold text-emerald-600">
              {formatBRL(totalLiberado)}
            </span>
          </p>
        )}
          </>
        )}
      </main>
    </div>
  );
}
